import React, { useState } from 'react';
import {
  Lock, Search, Filter, Download, Calendar, CheckCircle,
  XCircle, AlertCircle, Eye, FileText, History, User, Clock
} from 'lucide-react';

const ClosingAuditTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('period-lock');
  const [searchQuery, setSearchQuery] = useState('');

  const subTabs = [
    { id: 'period-lock', label: 'Period Lock' },
    { id: 'audit-trail', label: 'Audit Trail' },
    { id: 'closing', label: 'Period Closing' }
  ];

  const periods = [
    {
      id: 'PER-2024-02',
      period: 'February 2024',
      startDate: '2024-02-01',
      endDate: '2024-02-29',
      status: 'open',
      lockedDate: null,
      lockedBy: null,
      transactions: 1250,
      lastTransaction: '2024-02-11 15:30:00'
    },
    {
      id: 'PER-2024-01',
      period: 'January 2024',
      startDate: '2024-01-01',
      endDate: '2024-01-31',
      status: 'locked',
      lockedDate: '2024-02-05 18:00:00',
      lockedBy: 'Admin User',
      transactions: 1180,
      lastTransaction: '2024-01-31 23:59:59'
    },
    {
      id: 'PER-2023-12',
      period: 'December 2023',
      startDate: '2023-12-01',
      endDate: '2023-12-31',
      status: 'closed',
      lockedDate: '2024-01-05 18:00:00',
      lockedBy: 'Finance Manager',
      transactions: 1120,
      lastTransaction: '2023-12-31 23:59:59',
      closedDate: '2024-01-10 10:00:00',
      closedBy: 'Finance Manager'
    },
    {
      id: 'PER-2023-11',
      period: 'November 2023',
      startDate: '2023-11-01',
      endDate: '2023-11-30',
      status: 'closed',
      lockedDate: '2023-12-05 18:00:00',
      lockedBy: 'Finance Manager',
      transactions: 1080,
      lastTransaction: '2023-11-30 23:59:59',
      closedDate: '2023-12-10 10:00:00',
      closedBy: 'Finance Manager'
    }
  ];

  const auditLog = [
    {
      id: 'AUD-001',
      timestamp: '2024-02-11 15:30:00',
      actor: 'Finance User',
      actorId: 'USER-123',
      action: 'CREATE_JOURNAL_ENTRY',
      entityType: 'JournalEntry',
      entityId: 'JE-2024-001',
      originalValue: null,
      newValue: 'Monthly depreciation entry',
      ipAddress: '192.168.1.100',
      status: 'success'
    },
    {
      id: 'AUD-002',
      timestamp: '2024-02-11 14:20:00',
      actor: 'Admin User',
      actorId: 'USER-001',
      action: 'LOCK_PERIOD',
      entityType: 'Period',
      entityId: 'PER-2024-01',
      originalValue: 'open',
      newValue: 'locked',
      ipAddress: '192.168.1.50',
      status: 'success'
    },
    {
      id: 'AUD-003',
      timestamp: '2024-02-11 13:15:00',
      actor: 'Finance User',
      actorId: 'USER-123',
      action: 'UPDATE_ACCOUNT',
      entityType: 'ChartOfAccounts',
      entityId: '1110',
      originalValue: 'Cash Account',
      newValue: 'Cash and Cash Equivalents',
      ipAddress: '192.168.1.100',
      status: 'success'
    },
    {
      id: 'AUD-004',
      timestamp: '2024-02-11 12:00:00',
      actor: 'System',
      actorId: 'SYSTEM',
      action: 'AUTO_POST',
      entityType: 'JournalEntry',
      entityId: 'JE-2024-002',
      originalValue: null,
      newValue: 'Automated posting from WMS event',
      ipAddress: '127.0.0.1',
      status: 'success'
    },
    {
      id: 'AUD-005',
      timestamp: '2024-02-11 11:30:00',
      actor: 'Finance User',
      actorId: 'USER-123',
      action: 'REVERSE_ENTRY',
      entityType: 'JournalEntry',
      entityId: 'JE-2024-000',
      originalValue: 'Posted entry',
      newValue: 'Reversed entry',
      ipAddress: '192.168.1.100',
      status: 'success'
    },
    {
      id: 'AUD-006',
      timestamp: '2024-02-11 10:00:00',
      actor: 'Admin User',
      actorId: 'USER-001',
      action: 'DELETE_ATTEMPT',
      entityType: 'Posting',
      entityId: 'POST-12345',
      originalValue: 'Ledger entry',
      newValue: null,
      ipAddress: '192.168.1.50',
      status: 'blocked'
    }
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">Open</span>;
      case 'locked':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700">Locked</span>;
      case 'closed':
        return <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">Closed</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">Unknown</span>;
    }
  };

  const getActionBadge = (action) => {
    const actionColors = {
      'CREATE_JOURNAL_ENTRY': 'bg-blue-100 text-blue-700',
      'LOCK_PERIOD': 'bg-yellow-100 text-yellow-700',
      'UPDATE_ACCOUNT': 'bg-purple-100 text-purple-700',
      'AUTO_POST': 'bg-green-100 text-green-700',
      'REVERSE_ENTRY': 'bg-orange-100 text-orange-700',
      'DELETE_ATTEMPT': 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${actionColors[action] || 'bg-gray-100 text-gray-700'}`}>
        {action.replace(/_/g, ' ')}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Period Lock */}
      {activeSubTab === 'period-lock' && (
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">Period Locking</h3>
                <p className="text-sm text-yellow-700">
                  Locking a period prevents any new transactions from being posted to that period. 
                  This is essential for accurate financial reporting and audit compliance.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search periods..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Accounting Periods</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-4">Period</th>
                    <th className="text-left py-3 px-4">Date Range</th>
                    <th className="text-center py-3 px-4">Status</th>
                    <th className="text-right py-3 px-4">Transactions</th>
                    <th className="text-left py-3 px-4">Last Transaction</th>
                    <th className="text-left py-3 px-4">Locked By</th>
                    <th className="text-left py-3 px-4">Locked Date</th>
                    <th className="text-center py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.map((period) => (
                    <tr key={period.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{period.period}</td>
                      <td className="py-3 px-4 text-gray-600">
                        {period.startDate} to {period.endDate}
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(period.status)}</td>
                      <td className="py-3 px-4 text-right text-gray-900">{period.transactions}</td>
                      <td className="py-3 px-4 text-gray-600">{period.lastTransaction}</td>
                      <td className="py-3 px-4 text-gray-600">{period.lockedBy || '-'}</td>
                      <td className="py-3 px-4 text-gray-600">{period.lockedDate || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          {period.status === 'open' && (
                            <button className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg text-xs font-medium hover:bg-yellow-700">
                              Lock Period
                            </button>
                          )}
                          {period.status === 'locked' && (
                            <button className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
                              Close Period
                            </button>
                          )}
                          <button className="p-1.5 hover:bg-gray-200 rounded">
                            <Eye className="h-4 w-4 text-gray-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Audit Trail */}
      {activeSubTab === 'audit-trail' && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <History className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Complete Audit Trail</h3>
                <p className="text-sm text-blue-700">
                  Every action in the financial system is logged with timestamp, user, IP address, and change details.
                  This provides complete traceability for compliance and audit purposes.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search audit log..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white">
                <option value="all">All Actions</option>
                <option value="CREATE">Create</option>
                <option value="UPDATE">Update</option>
                <option value="DELETE">Delete</option>
                <option value="LOCK">Lock</option>
                <option value="REVERSE">Reverse</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Audit Log</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-4">Timestamp</th>
                    <th className="text-left py-3 px-4">Actor</th>
                    <th className="text-left py-3 px-4">Action</th>
                    <th className="text-left py-3 px-4">Entity</th>
                    <th className="text-left py-3 px-4">Entity ID</th>
                    <th className="text-left py-3 px-4">Original Value</th>
                    <th className="text-left py-3 px-4">New Value</th>
                    <th className="text-left py-3 px-4">IP Address</th>
                    <th className="text-center py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map((log) => (
                    <tr key={log.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span className="text-gray-900">{log.timestamp}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900">{log.actor}</p>
                            <p className="text-xs text-gray-500">{log.actorId}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">{getActionBadge(log.action)}</td>
                      <td className="py-3 px-4 text-gray-900">{log.entityType}</td>
                      <td className="py-3 px-4">
                        <code className="text-xs text-gray-600 font-mono">{log.entityId}</code>
                      </td>
                      <td className="py-3 px-4 text-gray-600 max-w-xs truncate">
                        {log.originalValue || '-'}
                      </td>
                      <td className="py-3 px-4 text-gray-900 max-w-xs truncate">
                        {log.newValue || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <code className="text-xs text-gray-600 font-mono">{log.ipAddress}</code>
                      </td>
                      <td className="py-3 px-4">
                        {log.status === 'success' ? (
                          <CheckCircle className="h-4 w-4 text-green-600 mx-auto" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Period Closing */}
      {activeSubTab === 'closing' && (
        <div className="space-y-6">
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-purple-900 mb-1">Period Closing</h3>
                <p className="text-sm text-purple-700">
                  Closing a period finalizes all transactions and generates financial statements.
                  Once closed, a period cannot be reopened without special authorization.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Close Period Checklist</h3>
            <div className="space-y-3">
              {[
                { label: 'All transactions posted for the period', checked: true },
                { label: 'Bank reconciliation completed', checked: true },
                { label: 'Accounts receivable reconciled', checked: true },
                { label: 'Accounts payable reconciled', checked: true },
                { label: 'Inventory reconciled', checked: true },
                { label: 'Depreciation entries posted', checked: true },
                { label: 'Trial balance balanced', checked: true },
                { label: 'Financial statements reviewed', checked: false }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  {item.checked ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-gray-400" />
                  )}
                  <span className={`text-sm ${item.checked ? 'text-gray-900' : 'text-gray-500'}`}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700">
                Close Period
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClosingAuditTab;
